import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const EditLeadDialog = ({ open, onOpenChange, lead, onSuccess }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    vehicle_interest: '',
    trade_in_vehicle: '',
    preferred_language: 'en',
    status: 'new'
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (lead) {
      setFormData({
        first_name: lead.first_name || '',
        last_name: lead.last_name || '',
        phone: lead.phone || '',
        email: lead.email || '',
        vehicle_interest: lead.vehicle_interest || '',
        trade_in_vehicle: lead.trade_in_vehicle || '',
        preferred_language: lead.preferred_language || 'en',
        status: lead.status || 'new'
      });
    }
  }, [lead]);

  const handleSave = async () => {
    // Validation
    if (!formData.first_name || !formData.last_name || !formData.phone) {
      toast.error('First name, last name, and phone are required');
      return;
    }

    setIsSaving(true);

    try {
      await axios.put(`${BACKEND_URL}/api/leads/${lead.id}`, formData);
      toast.success('Lead updated successfully');
      if (onSuccess) {
        onSuccess();
      }
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error(error.response?.data?.detail || 'Failed to update lead');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]" data-testid="edit-lead-dialog">
        <DialogHeader>
          <DialogTitle>Edit Lead</DialogTitle>
          <DialogDescription>
            Update lead information and status
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4 max-h-[500px] overflow-y-auto">
          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name *</Label>
              <Input
                id="first_name"
                placeholder="John"
                value={formData.first_name}
                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                data-testid="edit-lead-first-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name *</Label>
              <Input
                id="last_name"
                placeholder="Doe"
                value={formData.last_name}
                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                data-testid="edit-lead-last-name"
              />
            </div>
          </div>

          {/* Contact Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                placeholder="+1234567890"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                data-testid="edit-lead-phone"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                data-testid="edit-lead-email"
              />
            </div>
          </div>

          {/* Vehicle Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="vehicle_interest">Vehicle Interest</Label>
              <Input
                id="vehicle_interest"
                placeholder="2024 Honda Accord"
                value={formData.vehicle_interest}
                onChange={(e) => setFormData({...formData, vehicle_interest: e.target.value})}
                data-testid="edit-lead-vehicle-interest"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="trade_in_vehicle">Trade-in Vehicle</Label>
              <Input
                id="trade_in_vehicle"
                placeholder="2018 Toyota Camry"
                value={formData.trade_in_vehicle}
                onChange={(e) => setFormData({...formData, trade_in_vehicle: e.target.value})}
                data-testid="edit-lead-trade-in"
              />
            </div>
          </div>

          {/* Status & Language */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({...formData, status: value})}
              >
                <SelectTrigger data-testid="edit-lead-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="booked">Booked</SelectItem>
                  <SelectItem value="no_answer">No Answer</SelectItem>
                  <SelectItem value="dnc">Do Not Call</SelectItem>
                  <SelectItem value="low_priority">Low Priority</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="preferred_language">Preferred Language</Label>
              <Select
                value={formData.preferred_language}
                onValueChange={(value) => setFormData({...formData, preferred_language: value})}
              >
                <SelectTrigger data-testid="edit-lead-language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="fr">French</SelectItem>
                  <SelectItem value="es">Spanish</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
            data-testid="edit-lead-cancel-button"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={isSaving}
            data-testid="edit-lead-save-button"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
